/**
 * Production-ready error handling utilities
 */

/**
 * Extracts a user-friendly error message from various error formats
 * @param {Error|Object} error - The error object from a failed request
 * @param {string} defaultMessage - Fallback message if no specific error found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (
  error,
  defaultMessage = "An error occurred"
) => {
  // Check for response errors (API errors)
  if (error.response?.data) {
    const { data } = error.response;

    // Check common error message locations
    if (data.error) return data.error;
    if (data.message) return data.message;
    if (data.errors && Array.isArray(data.errors)) {
      return data.errors[0]?.message || data.errors[0] || defaultMessage;
    }
    if (typeof data === "string") return data;
  }

  // Check for network errors
  if (error.code === "ECONNABORTED") {
    return "Request timeout - please try again";
  }
  if (error.code === "ENETUNREACH") {
    return "Network unreachable - please check your connection";
  }

  // Check for standard error message
  if (error.message) return error.message;

  // Return default message
  return defaultMessage;
};

/**
 * Logs error details for debugging (production-safe)
 * @param {string} context - Where the error occurred
 * @param {Error|Object} error - The error object
 * @param {Object} metadata - Additional context data
 */
export const logError = (context, error, metadata = {}) => {
  const errorInfo = {
    context,
    timestamp: new Date().toISOString(),
    ...metadata,
  };

  console.error(`❌ [${context}]`, errorInfo, error);
};

/**
 * Validates required fields before API calls
 * @param {Object} data - Data to validate
 * @param {Array<string>} requiredFields - Field names that must be present
 * @returns {Object} Validation result
 */
export const validateRequired = (data, requiredFields) => {
  const errors = [];

  for (const field of requiredFields) {
    if (
      !data[field] ||
      (typeof data[field] === "string" && !data[field].trim())
    ) {
      errors.push(`${field} is required`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Sanitizes user input before sending to API
 * @param {Object} data - Data to sanitize
 * @returns {Object} Sanitized data
 */
export const sanitizeInput = (data) => {
  const sanitized = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue; // Skip null/undefined values
    }

    if (typeof value === "string") {
      // Trim whitespace and remove potential XSS
      sanitized[key] = value.trim().replace(/<script[^>]*>.*?<\/script>/gi, "");
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};
